import { styled } from 'baseui'
import type { Theme } from 'baseui'
import type { StyleObject } from 'styletron-standard'
import { pulseLighting } from '@/shared/theme/pulseLighting'
import { pulseOutline, pulseRadius } from '../styled-components'
import type { PrestationTone, StatusTone } from './contractStatusTone'

interface ThemeProps {
  $theme: Theme
}

// The last track fits "Ouvrir" plus the two icon buttons: each row is its own grid, so it can't size to content.
const TABLE_COLUMNS = 'minmax(150px, 0.9fr) minmax(180px, 1fr) minmax(170px, 1fr) minmax(170px, 1fr) 208px'

const statusToneColors = ($theme: Theme, $tone: StatusTone): StyleObject => {
  const { colors } = $theme
  if ($tone === 'ok') return { backgroundColor: colors.chipOkBg, color: colors.chipOkText }
  if ($tone === 'warn') return { backgroundColor: colors.chipWarnBg, color: colors.chipWarnText }
  if ($tone === 'err') return { backgroundColor: colors.noticeErrBg, color: colors.noticeErrText }
  if ($tone === 'info') return { backgroundColor: colors.bgPulseSelected, color: colors.primaryPulse }
  if ($tone === 'closed') return { backgroundColor: colors.chipNeutralBg, color: colors.textPulseStrong }
  return {
    backgroundColor: colors.white,
    color: colors.textPulseMuted,
    ...pulseOutline(colors.borderPulseHover, 'dashed'),
  }
}

const prestationToneColors = ($theme: Theme, $tone: PrestationTone): StyleObject => {
  const { colors } = $theme
  if ($tone === 'cdf') return { backgroundColor: colors.bgPulseSelected, color: colors.primaryPulse }
  if ($tone === 'mes') return { backgroundColor: colors.noticeAddSaleBg, color: colors.noticeAddSaleText }
  return { backgroundColor: colors.chipNeutralBg, color: colors.textPulseChipNeutral }
}

export const ContractsTable = styled('div', {
  marginTop: '14px',
  marginLeft: '-8px',
  marginRight: '-8px',
  overflowX: 'auto',
})

// The header row is the table's first child, so the odd children are every other contract.
export const ContractsTableRow = styled('div', ({ $theme }: ThemeProps) => ({
  display: 'grid',
  gridTemplateColumns: TABLE_COLUMNS,
  ...pulseRadius('10px'),
  backgroundColor: $theme.colors.white,
  transitionProperty: 'background-color',
  transitionDuration: '120ms',
  ':nth-child(odd)': { backgroundColor: $theme.colors.bgPulseCanvas },
  ':hover': { backgroundColor: $theme.colors.bgPulseSelected },
}))

export const ContractsHeaderRow = styled('div', ({ $theme }: ThemeProps) => ({
  display: 'grid',
  gridTemplateColumns: TABLE_COLUMNS,
  marginBottom: '4px',
  borderBottomWidth: '2px',
  borderBottomStyle: 'solid',
  borderBottomColor: $theme.colors.primaryPulse,
}))

const edgeCellPadding: StyleObject = {
  ':first-child': { paddingLeft: '14px' },
  ':last-child': { paddingRight: '14px' },
}

export const HeaderCell = styled('div', ({ $theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  height: '42px',
  paddingLeft: '12px',
  paddingRight: '12px',
  fontSize: '13px',
  fontWeight: 600,
  color: $theme.colors.primaryPulse,
  ...edgeCellPadding,
}))

export const SortButton = styled<'button', { $active: boolean }>('button', ({ $theme, $active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  height: '28px',
  marginLeft: '-6px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '6px',
  paddingRight: '6px',
  ...pulseOutline('transparent'),
  ...pulseRadius('6px'),
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  backgroundColor: $active ? $theme.colors.bgPulseSelected : 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  ':hover': { backgroundColor: $theme.colors.bgPulseCanvas },
  ':focus-visible': { boxShadow: pulseLighting($theme).shadowPulseFocus },
}))

export const SortIconSlot = styled<'span', { $active: boolean }>('span', ({ $active }) => ({
  display: 'inline-flex',
  opacity: $active ? 1 : 0.6,
}))

export const ContractsBand = styled('div', ({ $theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  minHeight: '32px',
  marginTop: '-16px',
  marginLeft: '-22px',
  marginRight: '-22px',
  paddingTop: '12px',
  paddingBottom: '12px',
  paddingLeft: '22px',
  paddingRight: '22px',
  borderTopLeftRadius: '15px',
  borderTopRightRadius: '15px',
  backgroundColor: $theme.colors.primaryPulse,
}))

export const BandTitle = styled('h2', ({ $theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexGrow: 1,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  fontSize: '15px',
  lineHeight: '20px',
  fontWeight: 600,
  color: $theme.colors.white,
}))

export const BodyCell = styled('div', ({ $theme }: ThemeProps) => ({
  minWidth: 0,
  paddingTop: '14px',
  paddingBottom: '14px',
  paddingLeft: '12px',
  paddingRight: '12px',
  fontSize: '14px',
  lineHeight: '20px',
  color: $theme.colors.textPulseStrong,
  overflowWrap: 'anywhere',
  ...edgeCellPadding,
}))

export const StatusChip = styled<'span', { $tone: StatusTone }>('span', ({ $theme, $tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  maxWidth: '100%',
  minHeight: '26px',
  paddingTop: '3px',
  paddingBottom: '3px',
  paddingLeft: '8px',
  paddingRight: '10px',
  boxSizing: 'border-box',
  ...pulseOutline('transparent'),
  ...pulseRadius('9999px'),
  fontSize: '12.5px',
  lineHeight: '18px',
  fontWeight: 600,
  ...statusToneColors($theme, $tone),
}))

export const ContractNumber = styled('span', ({ $theme }: ThemeProps) => ({
  display: 'block',
  marginTop: '8px',
  fontSize: '12px',
  lineHeight: '16px',
  color: $theme.colors.textPulseMuted,
}))

export const ContractNumberValue = styled('b', ({ $theme }: ThemeProps) => ({
  fontWeight: 600,
  color: $theme.colors.textPulseLabel,
  fontVariantNumeric: 'tabular-nums',
}))

export const StepList = styled('ul', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginTop: 0,
  marginBottom: 0,
  paddingLeft: 0,
  listStyleType: 'none',
})

export const StepItem = styled<'li', { $done: boolean }>('li', ({ $theme, $done }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13.5px',
  lineHeight: '18px',
  fontVariantNumeric: 'tabular-nums',
  color: $done ? $theme.colors.textPulseStrong : $theme.colors.textPulseMuted,
}))

export const StepDot = styled<'span', { $done: boolean }>('span', ({ $theme, $done }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '22px',
  height: '22px',
  boxSizing: 'border-box',
  ...pulseRadius('9999px'),
  ...($done
    ? {
        backgroundColor: $theme.colors.bgPulseSelected,
        color: $theme.colors.primaryPulse,
        ...pulseOutline('transparent'),
      }
    : {
        backgroundColor: $theme.colors.white,
        color: $theme.colors.textPulseFaint,
        ...pulseOutline($theme.colors.borderPulseHover, 'dashed'),
      }),
}))

export const MutedText = styled('span', ({ $theme }: ThemeProps) => ({
  color: $theme.colors.textPulseMuted,
}))

export const ContactName = styled('div', {
  fontWeight: 600,
})

export const ContactLastname = styled('span', {
  textTransform: 'uppercase',
})

export const SecondaryLine = styled('div', ({ $theme }: ThemeProps) => ({
  marginTop: '2px',
  fontSize: '13px',
  lineHeight: '18px',
  color: $theme.colors.textPulseLabel,
  overflowWrap: 'anywhere',
}))

export const PhoneLink = styled<'a', { $hidden: boolean }>('a', ({ $theme, $hidden }) => ({
  display: $hidden ? 'none' : 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  maxWidth: '100%',
  height: '30px',
  marginTop: '8px',
  paddingLeft: '8px',
  paddingRight: '10px',
  boxSizing: 'border-box',
  ...pulseOutline($theme.colors.borderPulseButton),
  ...pulseRadius('8px'),
  backgroundColor: $theme.colors.white,
  boxShadow: pulseLighting($theme).shadowPulseTab,
  color: $theme.colors.primaryPulse,
  fontSize: '13.5px',
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  overflowWrap: 'normal',
  textDecoration: 'none',
  ':hover': { backgroundColor: $theme.colors.bgPulseCanvas, ...pulseOutline($theme.colors.borderPulseControl) },
  ':focus-visible': { boxShadow: pulseLighting($theme).shadowPulseFocus },
}))

export const EmptyValue = styled('span', ({ $theme }: ThemeProps) => ({
  fontStyle: 'italic',
  color: $theme.colors.textPulseMuted,
}))

export const ProviderLine = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '6px 8px',
  fontWeight: 600,
})

export const PrestationChip = styled<'span', { $tone: PrestationTone; $hidden: boolean }>(
  'span',
  ({ $theme, $tone, $hidden }) => ({
    display: $hidden ? 'none' : 'inline-flex',
    alignItems: 'center',
    height: '20px',
    paddingLeft: '7px',
    paddingRight: '7px',
    ...pulseRadius('6px'),
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    ...prestationToneColors($theme, $tone),
  }),
)

export const OfferDetails = styled<'div', { $hidden: boolean }>('div', ({ $hidden }) => ({
  display: $hidden ? 'none' : 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: '8px',
}))

export const OfferDetail = styled('span', ({ $theme }: ThemeProps) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '24px',
  paddingLeft: '8px',
  paddingRight: '8px',
  boxSizing: 'border-box',
  ...pulseOutline($theme.colors.borderPulseCard),
  ...pulseRadius('6px'),
  backgroundColor: $theme.colors.white,
  fontSize: '12px',
  color: $theme.colors.textPulseMuted,
}))

export const OfferDetailValue = styled('b', ({ $theme }: ThemeProps) => ({
  fontWeight: 600,
  color: $theme.colors.textPulseStrong,
  fontVariantNumeric: 'tabular-nums',
}))

export const ActionsRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
})

export const PagerGroup = styled('nav', ({ $theme }: ThemeProps) => ({
  display: 'inline-flex',
  alignItems: 'stretch',
  height: '32px',
  boxSizing: 'border-box',
  ...pulseOutline($theme.colors.borderPulseCard),
  ...pulseRadius('8px'),
  overflow: 'hidden',
  backgroundColor: $theme.colors.white,
  boxShadow: pulseLighting($theme).shadowPulseTab,
}))

export const PagerButton = styled('button', ({ $theme }: ThemeProps) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '10px',
  paddingRight: '10px',
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  backgroundColor: 'transparent',
  color: $theme.colors.primaryPulse,
  fontFamily: 'inherit',
  fontSize: '13.5px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  ':hover': { backgroundColor: $theme.colors.bgPulseCanvas },
  ':focus-visible': { boxShadow: pulseLighting($theme).shadowPulseFocus },
  ':disabled': { color: $theme.colors.textPulseFaint, backgroundColor: 'transparent', cursor: 'default' },
}))

export const PagerPage = styled('span', ({ $theme }: ThemeProps) => ({
  display: 'inline-flex',
  alignItems: 'center',
  paddingLeft: '12px',
  paddingRight: '12px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  borderLeftColor: $theme.colors.borderPulseCard,
  borderRightColor: $theme.colors.borderPulseCard,
  backgroundColor: $theme.colors.bgPulseCanvas,
  color: $theme.colors.textPulseStrong,
  fontSize: '13px',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
}))

export const BottomPager = styled<'div', { $hidden: boolean }>('div', ({ $hidden }) => ({
  display: $hidden ? 'none' : 'flex',
  justifyContent: 'center',
  marginTop: '16px',
}))

export const StateBox = styled('div', ({ $theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  paddingTop: '48px',
  paddingBottom: '40px',
  paddingLeft: '16px',
  paddingRight: '16px',
  textAlign: 'center',
  color: $theme.colors.textPulseLabel,
}))

export const StateIcon = styled('span', ({ $theme }: ThemeProps) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  ...pulseRadius('14px'),
  backgroundColor: $theme.colors.bgPulseCanvas,
  color: $theme.colors.primaryPulse,
}))

export const StateTitle = styled('p', ({ $theme }: ThemeProps) => ({
  marginTop: 0,
  marginBottom: 0,
  fontSize: '15px',
  lineHeight: '20px',
  fontWeight: 600,
  color: $theme.colors.textPulseStrong,
}))

export const VisuallyHiddenText = styled('span', {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
})

export const fillActionButtonOverrides = {
  BaseButton: {
    style: {
      flexGrow: 1,
      height: '32px',
      paddingLeft: '12px',
      paddingRight: '12px',
      gap: '6px',
    },
  },
}

export const iconActionButtonOverrides = {
  BaseButton: {
    style: {
      width: '32px',
      height: '32px',
      flexShrink: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
}

export const abandonIconButtonOverrides = {
  BaseButton: {
    style: ({ $theme }: ThemeProps) => ({
      width: '32px',
      height: '32px',
      flexShrink: 0,
      paddingLeft: 0,
      paddingRight: 0,
      color: $theme.colors.textPulseLabel,
      ':hover': {
        backgroundColor: $theme.colors.noticeErrBg,
        color: $theme.colors.noticeErrText,
      },
    }),
  },
}
